/**
 * useLiteStore: the Pinia setup store holding LiteState (entries, components, selection, view) in Workspace API DTO
 * shape, plus runtime state that is not part of the DTOs: parsed models, undo/redo, busy flags, toasts and the bound
 * engine. Every mutation of components/selection/entry meta goes through commit(), which snapshots for undo.
 *
 * Semantics ported from the server / MARA (cited inline):
 *  - entry and component serials only grow (Scene.AddComponent, WorkspaceAPI entry serials)
 *  - toggleVisualization: Nanome.Domain Component.AddVisualization/HideVisualization/ShowVisualization
 *    (Assets/Scripts/src/Nanome.Domain/Scenes/Components/Component.cs:49-126) via ToggleRepresentation.cs:36-45
 *  - updateVisualization: Component.ReplaceVisualization (Component.cs:128-143), matched by kind
 *  - default components hidden flag: AddDefaultComponents.cs:154-172 (Water, Ion)
 */
import { computed, markRaw, reactive, ref, shallowRef, toRaw, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { SKYBOX_DEFAULT_KEY, SKYBOX_NIGHT_MODE_DEFAULT } from '@nanome-lite/look';
import type { NanomeModel } from '@nanome-lite/data';
// Constants come from the model file, not the package root: the root will re-export the Molstar-backed loaders
// once implemented, and a static import of it would pull Molstar into the UI bundle (the loaders are reached only
// through the lazy import in data-client.ts).
import { isNucleic, isProtein, Substructure } from '@nanome-lite/data/model';
import type { FilterContext } from '@nanome-lite/data';
import type { ColoringModel, FilterModel, VisualizationModel } from './dto';
import { filterEntrySerials, type Component, type ComponentInput, type Entry, type EntrySource, type Selection, type SelectionMode, type Tool, type ViewState } from './types';
import type { HoverInfo, PickResult, ViewerEngine } from './engine';
import { getDataClient, type DataClient } from './data-client';
import { isDefaultAtomistic, splitExt, visualizationIs } from './presets';

export const UNDO_CAP = 50;

export interface Toast { id: number; severity: 'error' | 'warn' | 'info' | 'success'; summary: string; detail?: string }

export interface EntryMeta { serial: number; name: string; currentModelSerial: number }
export interface Snapshot { entries: EntryMeta[]; components: Component[]; selection: Selection; nextComponentSerial: number }

export type SelectionState = 'none' | 'partial' | 'full';

const RCSB_ID = /^[0-9][a-z0-9]{3}$/i;

/** Deep clone of JSON-plain data that may contain Vue reactive proxies at any depth (structuredClone rejects proxies). */
export function clonePlain<T>(v: T): T {
  const raw = toRaw(v) as unknown;
  if (Array.isArray(raw)) return raw.map(clonePlain) as unknown as T;
  if (raw !== null && typeof raw === 'object') {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(raw)) out[k] = clonePlain((raw as Record<string, unknown>)[k]);
    return out as T;
  }
  return raw as T;
}

const cloneComponents = (cs: Component[]): Component[] => cs.map(c => ({
  ...c,
  filters: clonePlain(c.filters),
  visualizations: clonePlain(c.visualizations),
  entrySerials: [...c.entrySerials],
}));
const cloneSelection = (s: Selection): Selection => ({ byEntry: Object.fromEntries(Object.entries(s.byEntry).map(([k, v]) => [k, [...v]])) });

const sortedUnique = (a: Iterable<number>) => [...new Set(a)].sort((x, y) => x - y);

/** Thrown when the user declines the first-load notice; callers treat it as a silent cancel. */
export class LoadCancelled extends Error { constructor() { super('load cancelled'); this.name = 'LoadCancelled'; } }

export const useLiteStore = defineStore('lite', () => {
  // ---- DTO state ---------------------------------------------------------------------------------------------
  const entries = ref<Entry[]>([]);
  const components = ref<Component[]>([]);
  const selection = ref<Selection>({ byEntry: {} });
  const view = ref<ViewState>({ skyboxTheme: SKYBOX_DEFAULT_KEY, nightMode: SKYBOX_NIGHT_MODE_DEFAULT, tool: 'default', selectionMode: 'residue' });

  // ---- runtime state (not serialised) -----------------------------------------------------------------------
  /** entrySerial -> parsed model; values are markRaw so Vue never proxies typed arrays. */
  const models = reactive(new Map<number, NanomeModel>()) as Map<number, NanomeModel>;
  const undoStack = shallowRef<Snapshot[]>([]);
  const redoStack = shallowRef<Snapshot[]>([]);
  const busy = ref(0);
  const loadingNames = ref<string[]>([]);
  const toasts = ref<Toast[]>([]);
  const hover = shallowRef<HoverInfo | null>(null);
  const engine = shallowRef<ViewerEngine | null>(null);
  const engineReady = ref(false);
  const isPanelCollapsed = ref(false);
  /**
   * SelectionService._componentSerial: the component whose atoms 'Edit selection' loaded into the selection, so the
   * sequence menu offers Update instead of Create. Not undoable; dropped when the component goes away.
   */
  const activeComponentSerialRaw = ref<number | null>(null);
  let nextEntrySerial = 1;
  let nextComponentSerial = 1;
  let toastId = 1;
  /** last picked unit for shift-range extension: entry, chain, residue, atom */
  let anchor: { entrySerial: number; chainIndex: number; residueIndex: number; atomIndex: number } | null = null;
  /**
   * componentSerial -> atom indices per entry (sorted model atom indices from the data worker). Filters never change
   * on a live component (addComponent clones, restore replaces), so the cache only empties on frame changes, entry
   * removal and undo/redo. Evaluations in flight are keyed `${serial}:${entry}`; `atomGen` discards late results after
   * an invalidation and `atomCacheVersion` lets getters re-run when a result lands.
   */
  const atomCache = new Map<number, Map<number, Uint32Array>>();
  const atomPending = new Map<string, Promise<Uint32Array>>();
  let atomGen = 0;
  const atomCacheVersion = ref(0);
  let data: DataClient | null = null;

  // ---- getters -----------------------------------------------------------------------------------------------
  const isBusy = computed(() => busy.value > 0);
  const canUndo = computed(() => undoStack.value.length > 0);
  const canRedo = computed(() => redoStack.value.length > 0);
  const hasSelection = computed(() => Object.values(selection.value.byEntry).some(a => a.length > 0));
  const entryBySerial = (serial: number) => entries.value.find(e => e.serial === serial);
  const componentBySerial = (serial: number) => components.value.find(c => c.serial === serial);
  /** Components targeting exactly this entry (EntryComponents.vue:827-829 / viewer.componentGetForEntry). */
  const componentsForEntry = (entrySerial: number) => components.value.filter(c => c.entrySerials.length === 1 && c.entrySerials[0] === entrySerial);
  /** Components targeting zero or several entries (viewer.componentGetCrossEntry). */
  const crossEntryComponents = computed(() => components.value.filter(c => c.entrySerials.length !== 1));
  const activeComponentSerial = computed(() => {
    const s = activeComponentSerialRaw.value;
    return s !== null && components.value.some(c => c.serial === s) ? s : null;
  });

  // ---- infrastructure ----------------------------------------------------------------------------------------
  function toast(severity: Toast['severity'], summary: string, detail?: string) {
    const t: Toast = detail === undefined ? { id: toastId++, severity, summary } : { id: toastId++, severity, summary, detail };
    toasts.value.push(t);
    return t.id;
  }
  function dismissToast(id: number) { toasts.value = toasts.value.filter(t => t.id !== id); }

  async function ensureData(): Promise<DataClient> {
    if (!data) data = await getDataClient();
    return data;
  }

  function snapshot(): Snapshot {
    return {
      entries: entries.value.map(e => ({ serial: e.serial, name: e.name, currentModelSerial: e.currentModelSerial })),
      components: cloneComponents(components.value),
      selection: cloneSelection(selection.value),
      nextComponentSerial,
    };
  }

  /** Call before every undoable mutation. */
  function commit() {
    const stack = undoStack.value.slice(-(UNDO_CAP - 1));
    stack.push(snapshot());
    undoStack.value = stack;
    redoStack.value = [];
  }

  function restore(s: Snapshot) {
    const live = new Set(entries.value.map(e => e.serial));
    for (const meta of s.entries) {
      const e = entryBySerial(meta.serial);
      if (!e) continue;
      e.name = meta.name;
      if (e.currentModelSerial !== meta.currentModelSerial) applyFrame(e, meta.currentModelSerial);
    }
    components.value = cloneComponents(s.components).filter(c => c.entrySerials.every(es => live.has(es)));
    const byEntry: Record<number, number[]> = {};
    for (const [k, v] of Object.entries(s.selection.byEntry)) if (live.has(Number(k))) byEntry[Number(k)] = [...v];
    selection.value = { byEntry };
    // serials only grow: never go back below what was handed out
    nextComponentSerial = Math.max(nextComponentSerial, s.nextComponentSerial);
    invalidateAtomCache();
  }

  function undo() {
    const s = undoStack.value[undoStack.value.length - 1];
    if (!s) return;
    undoStack.value = undoStack.value.slice(0, -1);
    redoStack.value = [...redoStack.value, snapshot()];
    restore(s);
  }

  function redo() {
    const s = redoStack.value[redoStack.value.length - 1];
    if (!s) return;
    redoStack.value = redoStack.value.slice(0, -1);
    undoStack.value = [...undoStack.value, snapshot()].slice(-UNDO_CAP);
    restore(s);
  }

  // ---- entries -----------------------------------------------------------------------------------------------
  function describeModel(model: NanomeModel) {
    let protein = false, nucleic = false, ligand = false;
    const sub = model.residues.substructure;
    for (let i = 0; i < sub.length; i++) {
      const t = sub[i] ?? 0;
      if (isProtein(t)) protein = true;
      else if (isNucleic(t)) nucleic = true;
      else if (t === Substructure.Ligand) ligand = true;
    }
    return { containsProtein: protein, containsNucleic: nucleic, containsLigands: ligand };
  }

  /** AddDefaultComponents.cs port from the data layer; `hidden` comes with it (Water and Ion start hidden, :154-172). */
  async function makeDefaultComponents(entrySerial: number, model: NanomeModel, client: DataClient): Promise<Component[]> {
    const { components: inputs, hidden } = await client.defaultComponentsDetailed(entrySerial, model);
    return inputs.map((input: ComponentInput, i: number) => ({
      name: input.name,
      filters: input.filters,
      visualizations: input.visualizations,
      serial: nextComponentSerial++,
      hidden: hidden[i] ?? false,
      isDefault: true,
      entrySerials: filterEntrySerials(input.filters),
    }));
  }

  async function addEntry(bytes: Uint8Array, fileName: string, sourceKind: EntrySource['kind'], ref?: string): Promise<Entry> {
    busy.value++;
    loadingNames.value = [...loadingNames.value, fileName];
    try {
      const client = await ensureData();
      const loaded = await client.loadEntry(bytes, fileName);
      const { ext, gz } = splitExt(fileName);
      const serial = nextEntrySerial++;
      const source: EntrySource = { kind: sourceKind, fileName, ext: loaded.ext || ext, bytes, gz: loaded.gz ?? gz };
      if (ref !== undefined) source.ref = ref;
      const entry: Entry = {
        serial,
        name: loaded.name || splitExt(fileName).base,
        source,
        modelCount: Math.max(1, loaded.modelCount),
        currentModelSerial: 1,
        chains: [...loaded.model.chains.name],
        atomCount: loaded.model.atomCount,
        ...describeModel(loaded.model),
      };
      const defaults = await makeDefaultComponents(serial, loaded.model, client);
      models.set(serial, markRaw(loaded.model));
      entries.value.push(entry);
      components.value.push(...defaults);
      for (const w of loaded.warnings ?? []) toast('warn', entry.name, w);
      return entry;
    }
    catch (e) {
      toast('error', `Could not load ${fileName}`, e instanceof Error ? e.message : String(e));
      throw e;
    }
    finally {
      busy.value--;
      loadingNames.value = loadingNames.value.filter(n => n !== fileName);
    }
  }

  /**
   * Optional gate the app installs to ask for consent before the first structure is loaded (the beta notice).
   * Resolves true to continue. A declined load throws LoadCancelled and shows no toast.
   */
  let loadGuard: (() => Promise<boolean>) | null = null;
  function setLoadGuard(fn: (() => Promise<boolean>) | null) { loadGuard = fn; }
  async function ensureLoadAllowed(): Promise<void> {
    if (loadGuard && !(await loadGuard())) throw new LoadCancelled();
  }

  async function addEntryFromFile(file: File | { name: string; bytes: Uint8Array }): Promise<Entry> {
    // `Blob.bytes()` exists in lib.dom, so test the field's type rather than its presence
    await ensureLoadAllowed();
    const raw = (file as { bytes?: unknown }).bytes;
    const bytes = raw instanceof Uint8Array ? raw : new Uint8Array(await (file as File).arrayBuffer());
    return addEntry(bytes, file.name, 'file');
  }

  async function addEntryFromFiles(files: Iterable<File>): Promise<Entry[]> {
    const out: Entry[] = [];
    try { await ensureLoadAllowed(); } catch { return out; }
    for (const f of files) {
      try { out.push(await addEntryFromFile(f)); }
      catch { /* toasted in addEntry; keep loading the rest */ }
    }
    return out;
  }

  /** WorkspaceImportFile.vue:46-64 — the browser fetches https://files.rcsb.org/download/{ID}.cif */
  async function addEntryFromRcsb(id: string): Promise<Entry> {
    await ensureLoadAllowed();
    const pdbId = id.trim().toUpperCase();
    if (!RCSB_ID.test(pdbId)) { toast('error', 'Invalid PDB id', `"${id}" is not a 4-character PDB id`); throw new Error('invalid PDB id'); }
    busy.value++;
    try {
      const res = await fetch(`https://files.rcsb.org/download/${pdbId}.cif`);
      if (!res.ok) throw new Error(`RCSB returned ${res.status} for ${pdbId}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      return await addEntry(bytes, `${pdbId}.cif`, 'rcsb', pdbId);
    }
    catch (e) {
      if (!(e instanceof Error && /^RCSB/.test(e.message))) throw e;
      toast('error', `Could not fetch ${pdbId}`, e.message);
      throw e;
    }
    finally { busy.value--; }
  }

  async function addEntryFromUrl(url: string): Promise<Entry> {
    await ensureLoadAllowed();
    let u: URL;
    try { u = new URL(url.trim()); }
    catch { toast('error', 'Invalid URL', url); throw new Error('invalid URL'); }
    const fileName = decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() ?? '') || 'structure';
    busy.value++;
    try {
      const res = await fetch(u.toString());
      if (!res.ok) throw new Error(`fetch failed: ${res.status} ${res.statusText}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      return await addEntry(bytes, fileName, 'url', u.toString());
    }
    catch (e) {
      if (!(e instanceof Error && /^fetch failed/.test(e.message))) throw e;
      toast('error', `Could not fetch ${fileName}`, e.message);
      throw e;
    }
    finally { busy.value--; }
  }

  function removeEntry(entrySerial: number) {
    if (!entryBySerial(entrySerial)) return;
    commit();
    entries.value = entries.value.filter(e => e.serial !== entrySerial);
    components.value = components.value.filter(c => !c.entrySerials.includes(entrySerial));
    const { [entrySerial]: _dropped, ...rest } = selection.value.byEntry;
    selection.value = { byEntry: rest };
    const model = models.get(entrySerial);
    models.delete(entrySerial);
    if (model && data?.release) data.release(model).catch(() => { /* worker already gone */ });
    invalidateAtomCache();
    if (anchor?.entrySerial === entrySerial) anchor = null;
  }

  function renameEntry(entrySerial: number, name: string) {
    const e = entryBySerial(entrySerial);
    if (!e || !name.trim() || e.name === name) return;
    commit();
    e.name = name.trim();
  }

  /**
   * Frame switch: the main-thread model swaps `xyz` right away (the engine reads it synchronously when
   * currentModelSerial changes), then the data client switches its own copy (the worker's) so later filter
   * evaluations see the same frame.
   */
  function applyFrame(e: Entry, modelSerial: number) {
    const s = Math.min(Math.max(1, Math.round(modelSerial)), e.modelCount);
    const model = models.get(e.serial);
    if (model) {
      const frame = model.frames[s - 1];
      if (frame) model.xyz = frame;
      if (data) data.setFrame(model, s).catch((err: unknown) => toast('error', 'Frame switch failed', err instanceof Error ? err.message : String(err)));
    }
    e.currentModelSerial = s;
    invalidateAtomCache();
  }

  function setCurrentModel(entrySerial: number, modelSerial: number) {
    const e = entryBySerial(entrySerial);
    if (!e) return;
    const s = Math.min(Math.max(1, Math.round(modelSerial)), e.modelCount);
    if (s === e.currentModelSerial) return;
    commit();
    applyFrame(e, s);
  }
  const stepModel = (entrySerial: number, delta: number) => {
    const e = entryBySerial(entrySerial);
    if (!e || e.modelCount < 2) return;
    // wrap like Nanome's StepForward/StepBackward
    const next = ((e.currentModelSerial - 1 + delta) % e.modelCount + e.modelCount) % e.modelCount + 1;
    setCurrentModel(entrySerial, next);
  };

  // ---- components --------------------------------------------------------------------------------------------
  /** Visualization edits leave the filters (and so the atom set) alone; only the default flag changes. */
  function touch(c: Component) { c.isDefault = false; }

  async function resetDefaultComponents(entrySerial: number) {
    const model = models.get(entrySerial);
    if (!model || !data) return;
    const defaults = await makeDefaultComponents(entrySerial, model, data);
    if (!models.has(entrySerial)) return;
    commit();
    components.value = [
      ...components.value.filter(c => !(c.entrySerials.length === 1 && c.entrySerials[0] === entrySerial)),
      ...defaults,
    ];
  }

  function removeAllComponents(entrySerial: number) {
    if (!componentsForEntry(entrySerial).length) return;
    commit();
    for (const c of componentsForEntry(entrySerial)) forgetComponentAtoms(c.serial);
    components.value = components.value.filter(c => !(c.entrySerials.length === 1 && c.entrySerials[0] === entrySerial));
  }

  function addComponent(input: ComponentInput, opts: { hidden?: boolean } = {}): Component {
    commit();
    const c: Component = {
      name: input.name,
      filters: clonePlain(input.filters),
      visualizations: clonePlain(input.visualizations),
      serial: nextComponentSerial++,
      hidden: opts.hidden ?? false,
      isDefault: false,
      entrySerials: filterEntrySerials(input.filters),
    };
    components.value.push(c);
    return c;
  }

  /**
   * Replace the whole component list in ONE undo step: a prompt result (packages/agent applyOps), a restored ChatGPT
   * widget state, an Open in Nanome handoff. Components must reference loaded entries (others are dropped) and their
   * entrySerials are recomputed from the filters. Components whose filters changed or that disappeared drop their
   * cached atom sets; the serial counter never goes backwards. `undoable: false` skips the history entry (restores).
   */
  function replaceComponents(next: Component[], opts: { nextComponentSerial?: number; undoable?: boolean } = {}) {
    if (opts.undoable !== false) commit();
    const before = new Map(components.value.map(c => [c.serial, JSON.stringify(toRaw(c.filters))]));
    const live = new Set(entries.value.map(e => e.serial));
    components.value = cloneComponents(next)
      .map(c => ({ ...c, entrySerials: filterEntrySerials(c.filters) }))
      .filter(c => c.entrySerials.every(es => live.has(es)));
    for (const [serial, filters] of before) {
      const now = componentBySerial(serial);
      if (!now || JSON.stringify(toRaw(now.filters)) !== filters) forgetComponentAtoms(serial);
    }
    const maxSerial = next.reduce((m, c) => Math.max(m, c.serial), 0);
    nextComponentSerial = Math.max(nextComponentSerial, maxSerial + 1, opts.nextComponentSerial ?? 0);
  }

  /** UpdateComponentFilter (SequencesMenuController.cs:50-59): new filters, same serial, visualizations kept. */
  function updateComponentFilters(serial: number, filters: FilterModel[]) {
    const c = componentBySerial(serial);
    if (!c || !filters.length) return;
    commit();
    c.filters = clonePlain(filters);
    c.entrySerials = filterEntrySerials(filters);
    c.isDefault = false;
    forgetComponentAtoms(serial);
  }

  function clearActiveComponent() { activeComponentSerialRaw.value = null; }

  /** The serial the next new component gets (serials only grow); pure appliers such as applyOps build on it. */
  function peekNextComponentSerial(): number { return nextComponentSerial; }

  function renameComponent(serial: number, name: string) {
    const c = componentBySerial(serial);
    if (!c || !name.trim() || c.name === name) return;
    commit();
    c.name = name.trim();
    c.isDefault = false;
  }

  function deleteComponent(serial: number) {
    if (!componentBySerial(serial)) return;
    commit();
    components.value = components.value.filter(c => c.serial !== serial);
    forgetComponentAtoms(serial);
  }

  /** toggle-hidden. Visibility is a scene property applied separately by Open in Nanome, so isDefault is kept. */
  function toggleHidden(serials: number[], hidden: boolean) {
    const targets = components.value.filter(c => serials.includes(c.serial) && c.hidden !== hidden);
    if (!targets.length) return;
    commit();
    for (const c of targets) c.hidden = hidden;
  }
  /** EntryRow eye: hide all unless all are already hidden (viewer.componentToggleForEntry). */
  function toggleEntryHidden(entrySerial: number) {
    const cs = componentsForEntry(entrySerial);
    if (!cs.length) return;
    toggleHidden(cs.map(c => c.serial), !cs.every(c => c.hidden));
  }

  /**
   * POST .../toggle-visualization semantics (ToggleRepresentation.cs:36-45 + Component.cs:59-126):
   * not contained (by Visualization.Is) -> add (a default atomistic replaces the existing default atomistic and
   * inherits its coloring + hydrogen mode); contained and visible -> hide; contained and hidden -> show.
   */
  function toggleVisualization(componentSerial: number, preset: Omit<VisualizationModel, 'visible'> & { visible?: boolean }) {
    const c = componentBySerial(componentSerial);
    if (!c) return;
    const incoming: VisualizationModel = { ...clonePlain(preset), visible: true } as VisualizationModel;
    const idx = c.visualizations.findIndex(v => visualizationIs(v, incoming));
    commit();
    touch(c);
    if (idx === -1) {
      if (isDefaultAtomistic(incoming)) {
        const existing = c.visualizations.find(v => isDefaultAtomistic(v));
        if (existing) {
          incoming.coloring = clonePlain(existing.coloring);
          if (existing.renderHydrogen !== undefined) incoming.renderHydrogen = existing.renderHydrogen;
        }
        c.visualizations = c.visualizations.filter(v => !isDefaultAtomistic(v));
      }
      else if (incoming.kind !== 'Atomistic' && incoming.kind !== 'Interaction') {
        // Component.cs:66-70: one visualization per non-atomistic type
        c.visualizations = c.visualizations.filter(v => v.kind !== incoming.kind);
      }
      c.visualizations.push(incoming);
      return;
    }
    const v = c.visualizations[idx]!;
    c.visualizations[idx] = { ...v, visible: !v.visible };
  }

  /** POST .../update-visualization: replace the visualization of the same kind (Component.cs:128-143). */
  function updateVisualization(componentSerial: number, visualization: VisualizationModel) {
    const c = componentBySerial(componentSerial);
    if (!c) return;
    const idx = visualization.kind === 'Interaction'
      ? c.visualizations.findIndex(v => visualizationIs(v, visualization))
      : c.visualizations.findIndex(v => v.kind === visualization.kind);
    if (idx === -1) return;
    commit();
    touch(c);
    c.visualizations[idx] = clonePlain(visualization);
  }

  /** ComponentColoring.vue:83-94 — apply a coloring preset; Element Type forces carbonsOnly false. */
  function setColoring(componentSerial: number, visualizationKind: VisualizationModel['kind'], coloring: ColoringModel) {
    const c = componentBySerial(componentSerial);
    const v = c?.visualizations.find(x => x.kind === visualizationKind);
    if (!c || !v) return;
    const next: ColoringModel = clonePlain(coloring);
    if (next.kind === 'ElementType') next.carbonsOnly = false;
    else if (next.carbonsOnly === undefined && v.coloring.carbonsOnly !== undefined) next.carbonsOnly = v.coloring.carbonsOnly;
    updateVisualization(componentSerial, { ...v, coloring: next });
  }

  function setCarbonsOnly(componentSerial: number, visualizationKind: VisualizationModel['kind'], carbonsOnly: boolean) {
    const v = componentBySerial(componentSerial)?.visualizations.find(x => x.kind === visualizationKind);
    if (!v || v.coloring.carbonsOnly === carbonsOnly) return;
    updateVisualization(componentSerial, { ...v, coloring: { ...v.coloring, carbonsOnly } });
  }

  function setUniformColor(componentSerial: number, visualizationKind: VisualizationModel['kind'], uniformColor: string) {
    const v = componentBySerial(componentSerial)?.visualizations.find(x => x.kind === visualizationKind);
    if (!v) return;
    updateVisualization(componentSerial, { ...v, coloring: { kind: 'Uniform', uniformColor: uniformColor.replace(/^#/, '').toUpperCase() } });
  }

  /** ComponentVisualizations.vue:83-95 */
  function setSurfaceOpacity(componentSerial: number, value: number | 'wireframe') {
    const v = componentBySerial(componentSerial)?.visualizations.find(x => x.kind === 'MolecularSurface');
    if (!v) return;
    const next: VisualizationModel = value === 'wireframe' ? { ...v, wireframe: true } : { ...v, wireframe: false, opacity: value };
    updateVisualization(componentSerial, next);
  }
  const setSurfaceWireframe = (componentSerial: number, wireframe: boolean) => {
    const v = componentBySerial(componentSerial)?.visualizations.find(x => x.kind === 'MolecularSurface');
    if (!v) return;
    updateVisualization(componentSerial, { ...v, wireframe });
  };

  /** ComponentVisualizations.vue:73-81 — every Atomistic visualization of the component. */
  function setHydrogenMode(componentSerial: number, mode: VisualizationModel['renderHydrogen'] & string) {
    const c = componentBySerial(componentSerial);
    if (!c) return;
    const targets = c.visualizations.filter(v => v.kind === 'Atomistic' && v.renderHydrogen !== mode);
    if (!targets.length) return;
    commit();
    touch(c);
    c.visualizations = c.visualizations.map(v => v.kind === 'Atomistic' ? { ...v, renderHydrogen: mode } : v);
  }

  // ---- selection ---------------------------------------------------------------------------------------------
  function setEntrySelection(entrySerial: number, atoms: number[]) {
    const byEntry = { ...selection.value.byEntry };
    if (atoms.length) byEntry[entrySerial] = atoms;
    else delete byEntry[entrySerial];
    selection.value = { byEntry };
  }

  function unitAtoms(model: NanomeModel, pick: PickResult, mode: SelectionMode): number[] {
    if (mode === 'atom') return pick.atomIndex === null ? residueAtoms(model, pick.residueIndex) : [pick.atomIndex];
    if (mode === 'chain') return chainAtoms(model, model.residues.chainIndex[pick.residueIndex] ?? 0);
    return residueAtoms(model, pick.residueIndex);
  }

  /** mode 'toggle': remove when every atom is already selected, else add. */
  function selectAtoms(entrySerial: number, atoms: Iterable<number>, mode: 'add' | 'remove' | 'toggle' | 'replace' = 'toggle') {
    const incoming = sortedUnique(atoms);
    if (!incoming.length && mode !== 'replace') return;
    const current = selection.value.byEntry[entrySerial] ?? [];
    const set = new Set(current);
    let effective = mode;
    if (mode === 'toggle') effective = incoming.every(a => set.has(a)) ? 'remove' : 'add';
    commit();
    if (effective === 'replace') { selection.value = { byEntry: incoming.length ? { [entrySerial]: incoming } : {} }; return; }
    if (effective === 'add') for (const a of incoming) set.add(a);
    else for (const a of incoming) set.delete(a);
    setEntrySelection(entrySerial, sortedUnique(set));
  }

  function selectResidues(entrySerial: number, residueIndices: number[], mode: 'add' | 'remove' | 'toggle' | 'replace' = 'toggle') {
    const model = models.get(entrySerial);
    if (!model) return;
    selectAtoms(entrySerial, residueIndices.flatMap(r => residueAtoms(model, r)), mode);
  }

  function selectChains(entrySerial: number, chainIndices: number[], mode: 'add' | 'remove' | 'toggle' | 'replace' = 'toggle') {
    const model = models.get(entrySerial);
    if (!model) return;
    selectAtoms(entrySerial, chainIndices.flatMap(c => chainAtoms(model, c)), mode);
  }

  function clearSelection() {
    if (!hasSelection.value) return;
    commit();
    selection.value = { byEntry: {} };
    anchor = null;
  }

  /**
   * Engine pick -> selection. Default tool: click toggles the whole residue. Selection tool: Atom / Residue / Chain
   * unit. Empty click clears. Shift extends from the last picked unit along the same chain (residue/atom index range).
   */
  function applyPick(pick: PickResult | null, ev: { shiftKey: boolean; ctrlKey: boolean }) {
    if (!pick) { clearSelection(); return; }
    const model = models.get(pick.entrySerial);
    if (!model) return;
    const mode: SelectionMode = view.value.tool === 'selection' ? view.value.selectionMode : 'residue';
    const chainIndex = model.residues.chainIndex[pick.residueIndex] ?? 0;
    const atomIndex = pick.atomIndex ?? (model.residues.atomStart[pick.residueIndex] ?? 0);
    if (ev.shiftKey && anchor && anchor.entrySerial === pick.entrySerial && mode !== 'chain' && anchor.chainIndex === chainIndex) {
      let atoms: number[];
      if (mode === 'atom') {
        const [a, b] = [Math.min(anchor.atomIndex, atomIndex), Math.max(anchor.atomIndex, atomIndex)];
        atoms = Array.from({ length: b - a + 1 }, (_, i) => a + i);
      }
      else {
        const [a, b] = [Math.min(anchor.residueIndex, pick.residueIndex), Math.max(anchor.residueIndex, pick.residueIndex)];
        atoms = [];
        for (let r = a; r <= b; r++) atoms.push(...residueAtoms(model, r));
      }
      selectAtoms(pick.entrySerial, atoms, 'add');
    }
    else {
      selectAtoms(pick.entrySerial, unitAtoms(model, pick, mode), 'toggle');
    }
    anchor = { entrySerial: pick.entrySerial, chainIndex, residueIndex: pick.residueIndex, atomIndex };
  }

  // ---- component atom sets (data worker, cached) --------------------------------------------------------------
  const EMPTY_ATOMS = new Uint32Array(0);

  function filterContext(entrySerial: number, e: Entry): FilterContext {
    return {
      entrySerial,
      currentModelSerial: e.currentModelSerial,
      otherEntries: entries.value.filter(o => o.serial !== entrySerial && models.has(o.serial)).map(o => ({ entrySerial: o.serial, model: models.get(o.serial)!, currentModelSerial: o.currentModelSerial })),
    };
  }

  function invalidateAtomCache() {
    atomGen++;
    atomCache.clear();
    atomPending.clear();
    atomCacheVersion.value++;
  }

  function forgetComponentAtoms(serial: number) {
    atomCache.delete(serial);
    for (const key of [...atomPending.keys()]) if (key.startsWith(`${serial}:`)) atomPending.delete(key);
    atomCacheVersion.value++;
  }

  /**
   * The atoms a component selects in one entry, evaluated once per (component, entry) by the data client and cached.
   * This is also the engine's filter evaluator (engine-binding.ts), so the renderer and the panel share one result.
   * Rejects when the data layer cannot evaluate the filters; the empty set is cached so it is not retried until the
   * next invalidation.
   */
  function componentAtomsFor(c: Component, entrySerial: number): Promise<Uint32Array> {
    const hit = atomCache.get(c.serial)?.get(entrySerial);
    if (hit) return Promise.resolve(hit);
    const key = `${c.serial}:${entrySerial}`;
    const pending = atomPending.get(key);
    if (pending) return pending;
    const model = models.get(entrySerial);
    const e = entryBySerial(entrySerial);
    if (!model || !e) return Promise.resolve(EMPTY_ATOMS);
    const gen = atomGen;
    const filters = clonePlain(c.filters);
    const settle = (atoms: Uint32Array) => {
      if (atomPending.get(key) !== promise || gen !== atomGen) return;
      atomPending.delete(key);
      let per = atomCache.get(c.serial);
      if (!per) { per = new Map(); atomCache.set(c.serial, per); }
      per.set(entrySerial, atoms);
      atomCacheVersion.value++;
    };
    const promise: Promise<Uint32Array> = ensureData()
      .then(client => client.evaluateFilters(filters, model, filterContext(entrySerial, e)))
      .then(atoms => { settle(atoms); return atoms; }, (err: unknown) => { settle(EMPTY_ATOMS); throw err; });
    atomPending.set(key, promise);
    return promise;
  }

  /** Every entry of the component, in parallel; evaluation failures count as no atoms. */
  async function componentAtoms(c: Component): Promise<Map<number, Uint32Array>> {
    const per = new Map<number, Uint32Array>();
    await Promise.all(c.entrySerials.map(async (es) => { per.set(es, await componentAtomsFor(c, es).catch(() => EMPTY_ATOMS)); }));
    return per;
  }

  /** Cached atoms only; kicks off the evaluation when missing and reports it through atomCacheVersion. */
  function cachedComponentAtoms(c: Component): Map<number, Uint32Array> {
    const per = new Map<number, Uint32Array>();
    for (const es of c.entrySerials) {
      const atoms = atomCache.get(c.serial)?.get(es);
      if (atoms) per.set(es, atoms);
      else if (models.has(es)) componentAtomsFor(c, es).catch(() => { /* reported by the engine's evaluator path */ });
    }
    return per;
  }

  function selectionStateOf(sets: Map<number, Uint32Array>[]): SelectionState {
    let full = 0, none = 0, partial = 0;
    for (const per of sets) {
      for (const [es, atoms] of per) {
        if (!atoms.length) continue;
        const sel = new Set(selection.value.byEntry[es] ?? []);
        let n = 0;
        for (let i = 0; i < atoms.length; i++) if (sel.has(atoms[i]!)) n++;
        if (n === 0) none++;
        else if (n === atoms.length) full++;
        else partial++;
      }
    }
    if (partial || (full && none)) return 'partial';
    if (full) return 'full';
    return 'none';
  }

  /**
   * viewer.componentSelectionState (store/viewer.js:548-558): 'none' | 'partial' | 'full' over the given components.
   * Synchronous for templates: reads the cache (atom sets still being evaluated count as unknown) and re-runs once
   * they land because it depends on atomCacheVersion.
   */
  function componentSelectionState(serials: number[]): SelectionState {
    void atomCacheVersion.value;
    const sets: Map<number, Uint32Array>[] = [];
    for (const serial of serials) {
      const c = componentBySerial(serial);
      if (c) sets.push(cachedComponentAtoms(c));
    }
    return selectionStateOf(sets);
  }

  /** Row click (ToggleComponentsSelection): fully selected -> deselect, else select all atoms of the components. */
  async function toggleComponentsSelection(serials: number[]) {
    const targets = serials.map(componentBySerial).filter((c): c is Component => !!c);
    const sets = await Promise.all(targets.map(componentAtoms));
    if (!targets.every((c, i) => componentBySerial(c.serial) === c && sets[i])) return; // changed while evaluating
    const state = selectionStateOf(sets);
    if (state === 'none' && !sets.some(per => [...per.values()].some(a => a.length))) return;
    commit();
    const byEntry = { ...selection.value.byEntry };
    for (const per of sets) {
      for (const [es, atoms] of per) {
        const set = new Set(byEntry[es] ?? []);
        if (state === 'full') for (let i = 0; i < atoms.length; i++) set.delete(atoms[i]!);
        else for (let i = 0; i < atoms.length; i++) set.add(atoms[i]!);
        const arr = sortedUnique(set);
        if (arr.length) byEntry[es] = arr; else delete byEntry[es];
      }
    }
    selection.value = { byEntry };
  }

  /** 'Edit selection' (SelectFromComponent): selection becomes exactly the component's atoms. */
  async function selectComponent(serial: number) {
    const c = componentBySerial(serial);
    if (!c) return;
    const per = await componentAtoms(c);
    if (componentBySerial(serial) !== c) return;
    commit();
    const byEntry: Record<number, number[]> = {};
    for (const [es, atoms] of per) if (atoms.length) byEntry[es] = Array.from(atoms);
    selection.value = { byEntry };
    activeComponentSerialRaw.value = serial;
  }

  // ---- batch edits (the prompt bar) ---------------------------------------------------------------------------
  // replaceComponents and peekNextComponentSerial (above, with the other component edits) serve the prompt bar too.

  /**
   * Atoms a filter pipeline matches, per entry (the entries its EntrySerial filters name, else every loaded entry).
   * Used for the prompt bar's `select` op; nothing is cached because the filters are one-off.
   */
  async function evaluateFilterAtoms(filters: FilterModel[]): Promise<Record<number, number[]>> {
    const client = await ensureData();
    const named = filterEntrySerials(filters);
    const targets = (named.length ? named : entries.value.map(e => e.serial)).filter(es => models.has(es));
    const out: Record<number, number[]> = {};
    await Promise.all(targets.map(async (es) => {
      const e = entryBySerial(es);
      if (!e) return;
      const atoms = await client.evaluateFilters(clonePlain(filters), models.get(es)!, filterContext(es, e));
      if (atoms.length) out[es] = Array.from(atoms);
    }));
    return out;
  }

  /** Replace the whole selection; `commit: false` when the caller already committed for this transaction. */
  function replaceSelection(byEntry: Record<number, number[]>, opts: { commit?: boolean } = {}) {
    if (opts.commit !== false) commit();
    const out: Record<number, number[]> = {};
    for (const [k, v] of Object.entries(byEntry)) if (v.length && entryBySerial(Number(k))) out[Number(k)] = sortedUnique(v);
    selection.value = { byEntry: out };
    anchor = null;
  }

  // ---- view / tools / environment ---------------------------------------------------------------------------
  function setTool(tool: Tool, mode?: SelectionMode) {
    view.value = { ...view.value, tool, selectionMode: mode ?? view.value.selectionMode };
  }
  function setSelectionMode(mode: SelectionMode) { setTool('selection', mode); }
  function setSkybox(opts: { theme?: string; nightMode?: boolean }) {
    view.value = { ...view.value, skyboxTheme: opts.theme ?? view.value.skyboxTheme, nightMode: opts.nightMode ?? view.value.nightMode };
  }

  // ---- engine passthroughs (zoom targets, snapshot) ----------------------------------------------------------
  const zoomEntry = (entrySerial: number) => engine.value?.focusEntry(entrySerial);
  const zoomComponent = (componentSerial: number) => engine.value?.focusComponent(componentSerial);
  const zoomAll = () => engine.value?.focusAll();
  const resetView = () => engine.value?.resetView();

  function setEngine(e: ViewerEngine | null) { engine.value = e ? markRaw(e) : null; engineReady.value = false; }
  function setHover(h: HoverInfo | null) { hover.value = h; }

  /** For tests and the engine binding: expose the lazily-resolved data client. */
  function dataClient(): DataClient | null { return data; }

  return {
    setLoadGuard,
    // state
    entries, components, selection, view, models, toasts, hover, engine, engineReady, isPanelCollapsed, loadingNames,
    undoStack, redoStack, atomCacheVersion, activeComponentSerial,
    // getters
    isBusy, canUndo, canRedo, hasSelection, crossEntryComponents, entryBySerial, componentBySerial, componentsForEntry,
    componentSelectionState, componentAtomsFor, componentAtoms, dataClient,
    // entries
    addEntryFromFile, addEntryFromFiles, addEntryFromRcsb, addEntryFromUrl, removeEntry, renameEntry, setCurrentModel, stepModel,
    // components
    resetDefaultComponents, removeAllComponents, addComponent, replaceComponents, peekNextComponentSerial, renameComponent, deleteComponent, toggleHidden, toggleEntryHidden,
    updateComponentFilters, clearActiveComponent,
    toggleVisualization, updateVisualization, setColoring, setCarbonsOnly, setUniformColor, setSurfaceOpacity, setSurfaceWireframe, setHydrogenMode,
    // selection
    selectAtoms, selectResidues, selectChains, clearSelection, applyPick, toggleComponentsSelection, selectComponent,
    // batch edits
    replaceSelection, evaluateFilterAtoms,
    // view
    setTool, setSelectionMode, setSkybox,
    // engine
    zoomEntry, zoomComponent, zoomAll, resetView, setEngine, setHover, invalidateAtomCache,
    // history, toasts
    undo, redo, commit, toast, dismissToast,
  };
});

export type LiteStore = ReturnType<typeof useLiteStore>;

// ---- model helpers (pure) --------------------------------------------------------------------------------------
export function residueAtoms(model: NanomeModel, residueIndex: number): number[] {
  const start = model.residues.atomStart[residueIndex];
  const count = model.residues.atomCount[residueIndex];
  if (start === undefined || count === undefined) return [];
  return Array.from({ length: count }, (_, i) => start + i);
}

export function chainAtoms(model: NanomeModel, chainIndex: number): number[] {
  const rs = model.chains.residueStart[chainIndex];
  const rc = model.chains.residueCount[chainIndex];
  if (rs === undefined || rc === undefined) return [];
  const out: number[] = [];
  for (let r = rs; r < rs + rc; r++) out.push(...residueAtoms(model, r));
  return out;
}

export type { Ref };
